import { ISize, IThickness, ScreenDirection } from "../../../domain/drawing";
import { IValueRange } from "../../../domain/system";

export interface IWizardBean extends IWizardBeanRegistationValidatable, IWizardbeanAttributesContainable<IWholeWizardAttributesBean> {
  pages: IWizardPageBean[];
  registration: IWizardRegistrationBean;
  registeredPages: IWizardPageBean[];
  scripts?: IWizardScriptBean[];
}

export interface IWizardbeanAttributesContainable<TAttributes extends IWizardAttributesBean = IWizardAttributesBean> {
  attributes?: IWizardAttributesBean;
}

export interface IWizardAttributesBean {
  fontSize?: string;
}

export interface IWholeWizardAttributesBean extends IWizardAttributesBean {
  scales?: number[];
}

export interface IWizardBeanRegistationValidatable {
  registerable?: IWizardValidatorBean[];
}

export interface IWizardBeanValidatable {
  enable?: IWizardValidatorBean[];
}

export interface IWizardBeanVisibilityValidatable {
  visible?: IWizardValidatorBean[];
}

export type WizardValidatorTypes
= "and"
| "or"
| "not"
| "is-text-inputted"
| "is-date-inputted"
| "is-selected"
| "is-media-selected"
| "is-user-selected"
| "is-referred-from"
| "is-option-selected";

export interface IWizardBeanTypeSpecifiable<T> {
  type: T;
}

export function instanceOfIWizardBeanTypeSpecifiable<T>(instance: any): instance is IWizardBeanTypeSpecifiable<T> {
  return instance
      && (instance.type !== undefined);
}

export interface IWizardValidatorBean extends IWizardBeanTypeSpecifiable<WizardValidatorTypes> {}

export function instanceOfIWizardValidatorBean(instance: any, type: WizardValidatorTypes): instance is IWizardValidatorBean {
  return instance
      && instanceOfIWizardBeanTypeSpecifiable<WizardValidatorTypes>(instance)
      && (instance.type === type);
}

export interface IWizardOperatorBean extends IWizardValidatorBean {
  validators: IWizardValidatorBean[];
}

export function instanceOfIWizardOperatorBean(instance: any, type: WizardValidatorTypes): instance is IWizardOperatorBean {
  return instance
      && (instance.validators !== undefined)
      && instanceOfIWizardValidatorBean(instance, type);
}

export interface IWizardAndOperatorBean extends IWizardOperatorBean {}

export function instanceOfIWizardAndOperatorBean(instance: any): instance is IWizardAndOperatorBean {
  return instance
      && instanceOfIWizardOperatorBean(instance, "and");
}

export interface IWizardOrOperatorBean extends IWizardOperatorBean {}

export function instanceOfIWizardOrOperatorBean(instance: any): instance is IWizardOrOperatorBean {
  return instance
      && instanceOfIWizardOperatorBean(instance, "or");
}

export interface IWizardUnaryOperatorBean extends IWizardValidatorBean {
  validator: IWizardValidatorBean;
}

export function instanceOfIWizardUnaryOperatorBean(instance: any, type: WizardValidatorTypes): instance is IWizardUnaryOperatorBean {
  return instance
      && (instance.validator !== undefined)
      && instanceOfIWizardValidatorBean(instance, type);
}

export interface IWizardNotOperatorBean extends IWizardUnaryOperatorBean {}

export function instanceOfIWizardNotOperatorBean(instance: any): instance is IWizardNotOperatorBean {
  return instance
      && instanceOfIWizardUnaryOperatorBean(instance, "not");
}

export interface IWizardFormatValidatorBean extends IWizardValidatorBean, IWizardBeanFormatIdSpecifiable {}

export function instanceOfIWizardFormatValidatorBean(instance: any, type: WizardValidatorTypes): instance is IWizardFormatValidatorBean {
  return instance
      && instanceOfIWizardValidatorBean(instance, type)
      && instanceOfIWizardBeanFormatIdSpecifiable(instance);
}

export interface IWizardBeanFormatIdSpecifiable {
  formatId: number;
}

export function instanceOfIWizardBeanFormatIdSpecifiable(instance: any): instance is IWizardBeanFormatIdSpecifiable {
  return instance
      && (instance.formatId !== undefined);
}

export interface IWizardBeanSizeSpecifiable {
  size: ISize<string>;
}

export function instanceOfIWizardBeanSizeSpecifiable(instance: any): instance is IWizardBeanSizeSpecifiable {
  return instance
      && (instance.size !== undefined);
}

export interface IWizardIsReferredFromFormatValidatorBean extends IWizardFormatValidatorBean {}

export function instanceOfIWizardIsReferredFromFormatValidatorBean(instance: any): instance is IWizardIsReferredFromFormatValidatorBean {
  return instance
      && instanceOfIWizardFormatValidatorBean(instance, "is-referred-from");
}

export interface IWizardObjectValidatorBean extends IWizardValidatorBean, IWizardBeanObjectIdSpecifiable {}

export function instanceOfIWizardObjectValidatorBean(instance: any, type: WizardValidatorTypes): instance is IWizardObjectValidatorBean {
  return instance
      && instanceOfIWizardValidatorBean(instance, type)
      && instanceOfIWizardBeanObjectIdSpecifiable(instance);
}

export interface IWizardBeanObjectIdSpecifiable {
  id: string; // obj-id.
}

export function instanceOfIWizardBeanObjectIdSpecifiable(instance: any): instance is IWizardBeanObjectIdSpecifiable {
  return instance
      && (instance.id !== undefined);
}

export interface IWizardIsTextInputtedValidatorBean extends IWizardObjectValidatorBean {}

export function instanceOfIWizardIsTextInputtedValidatorBean(instance: any): instance is IWizardIsTextInputtedValidatorBean {
  return instance
      && instanceOfIWizardObjectValidatorBean(instance, "is-text-inputted");
}

export interface IWizardIsDateInputtedValidatorBean extends IWizardObjectValidatorBean {}

export function instanceOfIWizardIsDateInputtedValidatorBean(instance: any): instance is IWizardIsDateInputtedValidatorBean {
  return instance
      && instanceOfIWizardObjectValidatorBean(instance, "is-date-inputted");
}

export interface IWizardIsSelectedValidatorBean extends IWizardObjectValidatorBean {}

export function instanceOfIWizardIsSelectedValidatorBean(instance: any): instance is IWizardIsSelectedValidatorBean {
  return instance
      && instanceOfIWizardObjectValidatorBean(instance, "is-selected");
}

export interface IWizardIsMediaSelectedValidatorBean extends IWizardObjectValidatorBean {}

export function instanceOfIWizardIsMediaSelectedValidatorBean(instance: any): instance is IWizardIsMediaSelectedValidatorBean {
  return instance
      && instanceOfIWizardObjectValidatorBean(instance, "is-media-selected");
}

export interface IWizardIsUserSelectedValidatorBean extends IWizardObjectValidatorBean {}

export function instanceOfIWizardIsUserSelectedValidatorBean(instance: any): instance is IWizardIsUserSelectedValidatorBean {
  return instance
      && instanceOfIWizardObjectValidatorBean(instance, "is-user-selected");
}

export interface IWizardIsOptionSelectedValidatorBean extends IWizardObjectValidatorBean, IWizardBeanTextSpecifiable {}

export function instanceOfIWizardIsOptionSelectedValidatorBean(instance: any): instance is IWizardIsOptionSelectedValidatorBean {
  return instance
    && instanceOfIWizardObjectValidatorBean(instance, "is-option-selected");
}

export interface IWizardPageBean extends IWizardBeanContentsContainable, IWizardBeanValidatable, IWizardBeanRegistationValidatable, IWizardbeanAttributesContainable {}

export interface IWizardBeanContentsContainable {
  contents: IWizardContentBean[];
  gap?: string;
  contentMargin?: string;
}

export function instanceOfIWizardBeanContentsContainable(instance: any): instance is IWizardBeanContentsContainable {
  return instance
      && (instance.contents !== undefined);
}

export interface IWizardRegistrationBean {
  isTemporary: boolean;
  shouldAuthenticate: boolean;
}

export interface IWizardScriptBean {
  allowed: string;
}

export interface IWizardContentBean extends IWizardBeanValidatable, IWizardBeanRegistationValidatable, IWizardBeanVisibilityValidatable, IWizardbeanAttributesContainable {
  margin?: IThickness<string>;
  isMandatory?: boolean;
}

export function instanceOfIWizardContentBean(instance: any): instance is IWizardContentBean {
  return instance;
}

export interface ITypedWizardContentBean<T> extends IWizardContentBean, IWizardBeanTypeSpecifiable<T> {
}

export function instanceOfITypedWizardContentBean<T>(instance: any, type: T): instance is ITypedWizardContentBean<T> {
  return instance
      && instanceOfIWizardBeanTypeSpecifiable<T>(instance)
      && (instance.type === type);
}

export interface IWizardContentContainerBean extends ITypedWizardContentBean<"container">, IWizardBeanContentsContainable {}

export function instanceOfIWizardContentContainerBean(instance: any): instance is IWizardContentContainerBean {
  return instance
      && instanceOfITypedWizardContentBean<"container">(instance, "container")
      && instanceOfIWizardBeanContentsContainable(instance);
}

export interface IWizardContentHorizontalContainerBean extends ITypedWizardContentBean<"horizontal-container">, IWizardBeanContentsContainable {}

export function instanceOfIWizardContentHorizontalContainerBean(instance: any): instance is IWizardContentHorizontalContainerBean {
  return instance
      && instanceOfITypedWizardContentBean<"horizontal-container">(instance, "horizontal-container")
      && instanceOfIWizardBeanContentsContainable(instance);
}

export interface IWizardContentStackContainerBean extends ITypedWizardContentBean<"stack-container">, IWizardBeanContentsContainable, IWizardBeanSizeSpecifiable {}

export function instanceOfIWizardContentStackContainerBean(instance: any): instance is IWizardContentStackContainerBean {
  return instance
      && instanceOfITypedWizardContentBean<"stack-container">(instance, "stack-container")
      && instanceOfIWizardBeanContentsContainable(instance)
      && instanceOfIWizardBeanSizeSpecifiable(instance);
}

export interface IWizardContentFixedTextIndicatorBean extends ITypedWizardContentBean<"fixed-text-indicator">, IWizardBeanTextSpecifiable {}

export function instanceOfIWizardContentFixedTextIndicatorBean(instance: any): instance is IWizardContentFixedTextIndicatorBean {
  return instance
      && instanceOfITypedWizardContentBean<"fixed-text-indicator">(instance, "fixed-text-indicator")
      && instanceOfIWizardBeanTextSpecifiable(instance);
}

export interface IWizardContentHeadingIndicatorBean extends ITypedWizardContentBean<"heading-indicator">, IWizardBeanTextSpecifiable {}

export function instanceOfIWizardContentHeadingIndicatorBean(instance: any): instance is IWizardContentHeadingIndicatorBean {
  return instance
      && instanceOfITypedWizardContentBean<"heading-indicator">(instance, "heading-indicator")
      && instanceOfIWizardBeanTextSpecifiable(instance);
}

export interface IWizardContentPatientIndicatorBean extends ITypedWizardContentBean<"patient-indicator"> {}

export function instanceOfIWizardContentPatientIndicatorBean(instance: any): instance is IWizardContentPatientIndicatorBean {
  return instance
      && instanceOfITypedWizardContentBean<"patient-indicator">(instance, "patient-indicator");
}

export interface IWizardContentFormatIndicatorBean extends ITypedWizardContentBean<"format-indicator"> {}

export function instanceOfIWizardContentFormatIndicatorBean(instance: any): instance is IWizardContentFormatIndicatorBean {
  return instance
      && instanceOfITypedWizardContentBean<"format-indicator">(instance, "format-indicator");
}

export interface IWizardContentNotImplementedBean extends ITypedWizardContentBean<"not-implemented"> {}

export function instanceOfIWizardContentNotImplementedBean(instance: any): instance is IWizardContentNotImplementedBean {
  return instance
      && instanceOfIWizardBeanTypeSpecifiable<WizardValidatorTypes>(instance);
}

export interface IWizardContentMediaIndicatorBean extends ITypedWizardContentBean<"media-indicator">, IWizardBeanObjectIdSpecifiable, IWizardBeanSizeSpecifiable, IWizardBeanTextSpecifiable, IWizardBeanProcessorContainable {}

export function instanceOfIWizardContentMediaIndicatorBean(instance: any): instance is IWizardContentMediaIndicatorBean {
  // size/textは未指定を許可する.
  return instance
      && instanceOfITypedWizardContentBean<"media-indicator">(instance, "media-indicator")
      && instanceOfIWizardBeanObjectIdSpecifiable(instance);
}

export interface IWizardBeanProcessorContainable {
  processor?: IWizardContentBean;
}

export interface IWizardContentTextIndicatorBean extends ITypedWizardContentBean<"text-indicator">, IWizardBeanObjectIdSpecifiable, IWizardBeanDecoratable {}

export function instanceOfIWizardContentTextIndicatorBean(instance: any): instance is IWizardContentTextIndicatorBean {
  return instance
      && instanceOfITypedWizardContentBean<"text-indicator">(instance, "text-indicator")
      && instanceOfIWizardBeanObjectIdSpecifiable(instance);
}

export interface IWizardBeanTextSpecifiable {
  text: string;
}

export function instanceOfIWizardBeanTextSpecifiable(instance: any): instance is IWizardBeanTextSpecifiable {
  return instance
      && (instance.text !== undefined);
}

export interface IWizardContentTextEditorBean extends ITypedWizardContentBean<"text-editor">, IWizardBeanObjectIdSpecifiable {}

export function instanceOfIWizardContentTextEditorBean(instance: any): instance is IWizardContentTextEditorBean {
  return instance
      && instanceOfITypedWizardContentBean<"text-editor">(instance, "text-editor")
      && instanceOfIWizardBeanObjectIdSpecifiable(instance);
}

export interface IWizardContentTextInputBean extends ITypedWizardContentBean<"text-input">, IWizardBeanObjectIdSpecifiable, IWizardBeanDecoratable {}

export function instanceOfIWizardContentTextInputBean(instance: any): instance is IWizardContentTextInputBean {
  return instance
      && instanceOfITypedWizardContentBean<"text-input">(instance, "text-input")
      && instanceOfIWizardBeanObjectIdSpecifiable(instance);
}

export interface IWizardBeanDecoratable {
  prefix?: string;
  postfix?: string;
}

export interface IWizardContentNumberInputBean extends ITypedWizardContentBean<"number-input">, IWizardBeanObjectIdSpecifiable, IWizardBeanDecoratable {}

export function instanceOfIWizardContentNumberInputBean(instance: any): instance is IWizardContentNumberInputBean {
  return instance
      && instanceOfITypedWizardContentBean<"number-input">(instance, "number-input")
      && instanceOfIWizardBeanObjectIdSpecifiable(instance);
}

export interface IWizardBeanBarDrawable {
  range: IValueRange;
  stepCount: number;
  direction: ScreenDirection;
  color: string;
}

export function instanceOfIWizardBeanBarDrawable(instance: any): instance is IWizardBeanBarDrawable {
  return instance
      && (instance.range !== undefined)
      && (instance.stepCount !== undefined)
      && (instance.direction !== undefined)
      && (instance.color !== undefined);
}

export interface IWizardContentBarInputBean extends ITypedWizardContentBean<"bar-input">, IWizardBeanObjectIdSpecifiable, IWizardBeanSizeSpecifiable, IWizardBeanBarDrawable {}

export function instanceOfIWizardContentBarInputBean(instance: any): instance is IWizardContentBarInputBean {
  return instance
      && instanceOfITypedWizardContentBean<"bar-input">(instance, "bar-input")
      && instanceOfIWizardBeanObjectIdSpecifiable(instance)
      && instanceOfIWizardBeanBarDrawable(instance);
}

export interface IWizardContentDateInputBean extends ITypedWizardContentBean<"date-input">, IWizardBeanObjectIdSpecifiable, IWizardBeanDecoratable {}

export function instanceOfIWizardContentDateInputBean(instance: any): instance is IWizardContentDateInputBean {
  return instance
      && instanceOfITypedWizardContentBean<"date-input">(instance, "date-input")
      && instanceOfIWizardBeanObjectIdSpecifiable(instance);
}

export interface IWizardContentRadioSelectorBean extends IWizardContentObjectSelectorBean<"radio-selector">, IWizardBeanObjectPositionSpecifiable {}

export function instanceOfIWizardContentRadioSelectorBean(instance: any): instance is IWizardContentRadioSelectorBean {
  return instance
      && instanceOfIWizardContentObjectSelectorBean<"radio-selector">(instance, "radio-selector");
}

export interface IWizardContentRadioSelectorsBean extends ITypedWizardContentBean<"radio-selectors">, IWizardBeanObjectIdSpecifiable, IWizardBeanObjectPositionSpecifiable, IWizardBeanObjectOptionsVisibilitySpecifiable, IWizardBeanObjectAlternativeOptionsSelectable {}

export function instanceOfIWizardContentRadioSelectorsBean(instance: any): instance is IWizardContentRadioSelectorsBean {
  return instance
    && instanceOfITypedWizardContentBean<"radio-selectors">(instance, "radio-selectors")
    && instanceOfIWizardBeanObjectIdSpecifiable(instance);
}

export interface IWizardContentCheckSelectorBean extends IWizardContentObjectSelectorBean<"check-selector">, IWizardBeanObjectPositionSpecifiable {}

export function instanceOfIWizardContentCheckSelectorBean(instance: any): instance is IWizardContentCheckSelectorBean {
  return instance
  && instanceOfIWizardContentObjectSelectorBean<"check-selector">(instance, "check-selector");
}

export interface IWizardContentObjectSelectorBean<T> extends ITypedWizardContentBean<T>, IWizardBeanObjectSelectable {}

export function instanceOfIWizardContentObjectSelectorBean<T>(instance: any, type: T): instance is IWizardContentObjectSelectorBean<T> {
  return instance
      && instanceOfITypedWizardContentBean<T>(instance, type)
      && instanceOfIWizardBeanObjectSelectable(instance);
}

export interface IWizardBeanObjectSelectable {
  options: IWizardObjectOptionBean[];
}

export function instanceOfIWizardBeanObjectSelectable(instance: any): instance is IWizardBeanObjectSelectable {
  return instance
      && (instance.options !== undefined);
}

export interface IWizardBeanObjectPositionSpecifiable {
  isHorizontal?: boolean;
}

export interface IWizardBeanObjectOptionsVisibilitySpecifiable {
  visibleOptionIndexes?: number[];
}

export interface IWizardBeanObjectAlternativeOptionsSelectable {
  alternativeOptions: IWizardObjectAlternativeOption[];
}

export interface IWizardObjectAlternativeOption {
  index: number;
  text: string;
}

export interface IWizardObjectOptionBean extends IWizardBeanObjectIdSpecifiable {
  text?: string;
  content?: IWizardContentBean;
}

export interface IWizardContentUserSelectorBean extends ITypedWizardContentBean<"user-selector">, IWizardBeanObjectIdSpecifiable, IWizardBeanMultiSelectable {}

export function instanceOfIWizardContentUserSelectorBean(instance: any): instance is IWizardContentUserSelectorBean {
  return instance
      && instanceOfITypedWizardContentBean<"user-selector">(instance, "user-selector")
      && instanceOfIWizardBeanObjectIdSpecifiable(instance);
}

export interface IWizardBeanMultiSelectable {
  isMultiSelectable: boolean;
}

export interface IWizardContentLoginUserSelectorBean extends ITypedWizardContentBean<"login-user-selector">, IWizardBeanObjectIdSpecifiable {}

export function instanceOfIWizardContentLoginUserSelectorBean(instance: any): instance is IWizardContentLoginUserSelectorBean {
  return instance
      && instanceOfITypedWizardContentBean<"login-user-selector">(instance, "login-user-selector")
      && instanceOfIWizardBeanObjectIdSpecifiable(instance);
}

export interface IWizardContentImageFileSelectorBean extends ITypedWizardContentBean<"image-file-selector">, IWizardBeanObjectIdSpecifiable {}

export function instanceOfIWizardContentImageFileSelectorBean(instance: any): instance is IWizardContentImageFileSelectorBean {
  return instance
      && instanceOfITypedWizardContentBean<"image-file-selector">(instance, "image-file-selector")
      && instanceOfIWizardBeanObjectIdSpecifiable(instance);
}

export interface IWizardContentImageCaptureInputBean extends ITypedWizardContentBean<"image-capture-input">, IWizardBeanObjectIdSpecifiable {}

export function instanceOfIWizardContentImageCaptureInputBean(instance: any): instance is IWizardContentImageCaptureInputBean {
  return instance
      && instanceOfITypedWizardContentBean<"image-capture-input">(instance, "image-capture-input")
      && instanceOfIWizardBeanObjectIdSpecifiable(instance);
}

export interface IWizardContentImageSignInputBean extends ITypedWizardContentBean<"image-sign-input">, IWizardBeanObjectIdSpecifiable, IWizardBeanSizeSpecifiable {}

export function instanceOfIWizardContentImageSignInputBean(instance: any): instance is IWizardContentImageSignInputBean {
  // sizeは未指定を許可する.
  return instance
      && instanceOfITypedWizardContentBean<"image-sign-input">(instance, "image-sign-input")
      && instanceOfIWizardBeanObjectIdSpecifiable(instance);
}

export interface IWizardContentClaioImageSelectorBean extends ITypedWizardContentBean<"claio-image-selector">, IWizardBeanObjectIdSpecifiable {}

export function instanceOfIWizardContentClaioImageSelectorBean(instance: any): instance is IWizardContentClaioImageSelectorBean {
  return instance
      && instanceOfITypedWizardContentBean<"claio-image-selector">(instance, "claio-image-selector")
      && instanceOfIWizardBeanObjectIdSpecifiable(instance);
}

export interface IWizardContentClaioSchemaSelectorBean extends ITypedWizardContentBean<"claio-schema-selector">, IWizardBeanObjectIdSpecifiable {}

export function instanceOfIWizardContentClaioSchemaSelectorBean(instance: any): instance is IWizardContentClaioSchemaSelectorBean {
  return instance
      && instanceOfITypedWizardContentBean<"claio-schema-selector">(instance, "claio-schema-selector")
      && instanceOfIWizardBeanObjectIdSpecifiable(instance);
}

export interface IWizardContentMediaRemoverBean extends ITypedWizardContentBean<"media-remover">, IWizardBeanObjectIdSpecifiable {}

export function instanceOfIWizardContentMediaRemoverBean(instance: any): instance is IWizardContentMediaRemoverBean {
  return instance
      && instanceOfITypedWizardContentBean<"media-remover">(instance, "media-remover")
      && instanceOfIWizardBeanObjectIdSpecifiable(instance);
}

export interface IWizardContentMediaRotaterBean extends ITypedWizardContentBean<"media-rotater">, IWizardBeanObjectIdSpecifiable, IWizardBeanRotatable {}

export function instanceOfIWizardContentMediaRotaterBean(instance: any): instance is IWizardContentMediaRotaterBean {
  return instance
      && instanceOfITypedWizardContentBean<"media-rotater">(instance, "media-rotater")
      && instanceOfIWizardBeanObjectIdSpecifiable(instance);
}

export interface IWizardBeanRotatable {
  angle?: number;
}
